/**
 * Pure FE transform: group the normalized activity stream by the calendar day of
 * `occurred_at` (ADR §5 grouping key).
 *
 * Ordering contract (UI-SPEC "Day Grouping"):
 *  - day groups are NEWEST-first;
 *  - within a day, events are DESCENDING by `occurred_at`;
 *  - `approximate` (date-only / reconstructed-time) rows sort to the END of their day
 *    group (their intra-day time is unknown);
 *  - the sort is STABLE — rows with equal timestamps preserve input order.
 *
 * Day KEY is granularity-aware (LR-208 r6 H2/M13): a date-only anchor
 * (`YYYY-MM-DD` from a DateField — transfer/hold/release/notice/the lossy
 * terminal row) is keyed by its LITERAL calendar date (zone-independent); a
 * full datetime is keyed by its UTC calendar day so the header day agrees with
 * the row's UTC time (`formatTimeUtc`). The label is derived FROM the key (not a
 * fresh parse of occurred_at) so the two can never drift. Pre-fix both used
 * LOCAL Date components, so date-only legal/lifecycle events and near-midnight
 * datetimes bucketed under the wrong day for any UTC-negative viewer.
 */
import { formatDate } from '@/utils/format';
import type { ActivityEvent } from '@/types';

export interface ActivityDayGroup {
  /** Local-Date-derived YYYY-MM-DD key (matches the formatDate display day). */
  dayKey: string;
  /** Human day label, e.g. "May 23, 2026" (from formatDate — same Date as the key). */
  dayLabel: string;
  events: ActivityEvent[];
}

/**
 * Build the day key. A date-only anchor (`YYYY-MM-DD`) is used verbatim (the
 * literal calendar date, zone-independent); a datetime is keyed by its UTC
 * calendar day (matches `formatTimeUtc`). Unparseable input → a stable
 * "0000-00-00" bucket (sorts to the end) rather than "NaN-NaN-NaN".
 */
function dayKeyOf(iso: string): string {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (dateOnly) return `${dateOnly[1]}-${dateOnly[2]}-${dateOnly[3]}`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '0000-00-00';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

interface Decorated {
  event: ActivityEvent;
  /** original input index — final stable tie-break */
  index: number;
  /** epoch ms of occurred_at */
  time: number;
}

/**
 * Group + order the activity stream by day.
 * Stable decorate-sort-undecorate: the original input index is the final tie-break so
 * equal-timestamp rows keep their relative input order regardless of the engine's sort.
 */
export function groupActivityByDay(events: ActivityEvent[]): ActivityDayGroup[] {
  if (events.length === 0) return [];

  const buckets = new Map<string, Decorated[]>();
  const keyOrder: string[] = [];

  events.forEach((event, index) => {
    const key = dayKeyOf(event.occurred_at);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = [];
      buckets.set(key, bucket);
      keyOrder.push(key);
    }
    const t = new Date(event.occurred_at).getTime();
    bucket.push({ event, index, time: Number.isNaN(t) ? 0 : t });
  });

  // day groups newest-first (descending by key — YYYY-MM-DD sorts lexicographically == chronologically)
  const sortedKeys = [...keyOrder].sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));

  return sortedKeys.map((key) => {
    const decorated = buckets.get(key)!;
    const sorted = [...decorated].sort((a, b) => {
      const aApprox = a.event.approximate;
      const bApprox = b.event.approximate;
      // approximate rows sink to the end of the day
      if (aApprox !== bApprox) return aApprox ? 1 : -1;
      if (aApprox && bApprox) {
        // both approximate: preserve input order (intra-day time is unknown)
        return a.index - b.index;
      }
      // both timestamped: descending by time, stable on ties via input index
      if (a.time !== b.time) return b.time - a.time;
      return a.index - b.index;
    });

    return {
      dayKey: key,
      // Derive the label FROM the key (a bare YYYY-MM-DD) so the displayed day
      // can never drift from the bucket key (LR-208 r6 H2).
      dayLabel: formatDate(key),
      events: sorted.map((d) => d.event),
    };
  });
}

/**
 * Flattened display order of the WHOLE stream — exactly the order groupActivityByDay
 * produces (day groups newest-first; within a day timestamped-desc, then `approximate`
 * rows sunk to the end; stable on input index). Order BEFORE paginating with this so a
 * page slice agrees with the grouping: an approximate (date-only `T00:00:00`) row can no
 * longer be paginated to a different page than where its day-group sinks it (M6).
 */
export function orderActivityForDisplay(events: ActivityEvent[]): ActivityEvent[] {
  return groupActivityByDay(events).flatMap((g) => g.events);
}
